import { deriveIntentForPlanningReport } from '../generationImpact/selectedAssessmentIntent';
/**
 * coverKpiChips — TCP-4 (row 0.99233) — shared KPI chip resolver
 * consumed by every cover variant (Classic / Hero / Minimal /
 * DataDense). Pre-TCP-4 each variant emitted hard-coded copy + a
 * private resolver pair (only DataDense + ClassicCover-branch had
 * the canonical chain). Hero + Minimal carried no KPI surface even
 * though the live-prod walkthrough flagged the missing chips.
 *
 * The resolver routes ALL chip sources through one canonical chain:
 *
 *   - POWER REQUIREMENT  →  effectivePower(intent)         [lib/projectIntent.ts]
 *                           →  phased MW / grid proxy      (report-type fallbacks)
 *   - COOLING            →  normaliseCooling + LABEL       [types/cooling.ts]
 *                           →  climate water-stress proxy  (water-resource)
 *   - ACRES              →  effectiveLandAcres(intent)     (defined here)
 *                           →  siteArea / datasets.land     (report snapshot)
 *   - BTM                →  effectiveBtm(intent)           (defined here)
 *                           →  btmDetailedAnalysis          (power-availability)
 *   - LCOE               →  effectiveLcoe(report)          (defined here)
 *                           →  btm/lcoe analysis rows       (report sections)
 *
 * Report-type profiles (`REPORT_TYPE_CHIP_PROFILES`) tailor which chips
 * each variant surfaces and which fallbacks apply. Proxy values are
 * prefixed with "Est." and never state unverified grid MW as fact.
 *
 * Each resolver returns `null` on undecided / missing so callers can
 * collapse the chip strip when ALL chips fall back to em-dash. Per
 * R1 in the pack's risk register the resolvers never throw — a
 * partial intent must still produce a clean cover.
 *
 * Sentinel value contract: a chip with `value === null` MAY still
 * render as a placeholder ("—") at the variant's discretion. Variants
 * that want to suppress an undecided chip entirely should `.filter`
 * on `value !== null` before mapping.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import { POWER_BUCKET_LABEL, TIMELINE_BUCKET_LABEL, effectivePhases, effectivePower, formatPrimaryBtmForCover } from '../projectIntent';
import { formatApproxLcoeAuthored } from '../lcoeDisplay';
import { parseWaterStressChipLabel } from '../parseWaterStressChipLabel';
import { getBtmDecisionState, getDecisionStateLabel, pickLeadingBtmOption } from '../btmDecision';
import { normaliseCooling, COOLING_TYPE_LABEL } from '../../types/cooling';
import { powerAvailabilityFromIntent, testFitFromIntent } from '../intentToPreferences';
import { getDisplayVerdict, tierLabel } from '../reportVerdict';
import type {
  SiteRagReport,
  ProjectIntent,
  TestFitPreferences,
  PowerAvailabilityPreferences,
} from '../../types/siteRag';
import { geometryDerivedAcres } from './journalCoverData';

function formatSentimentBand(band: string | undefined): string | null {
  if (!band?.trim()) return null;
  const normalized = band.trim().toLowerCase();
  if (normalized === 'supportive') return 'Supportive';
  if (normalized === 'mixed') return 'Mixed';
  if (normalized === 'opposed') return 'Opposed';
  return band.trim();
}

export interface CoverChip {
  /**
   * Stable id — used as React key, for testid suffixes, AND as the key the
   * tooltip builder (`lib/metrics/coverChipExplanations.ts`) explains the chip
   * by. The first five are the generic site metrics `resolveCoverChips`
   * produces; `npv` / `irr` / `payback` are the Financial Viability money
   * metrics.
   *
   * D17 (audit 2026-08-19): the financial cover used to REUSE `btm`/`cooling`/
   * `acres` for NPV / IRR / Payback, so hovering the NPV chip explained
   * "nuclear interest" and the Payback chip explained "land acres". Money
   * metrics now own their ids, so label, tooltip and value always agree.
   */
  id: 'power' | 'cooling' | 'acres' | 'btm' | 'lcoe' | 'npv' | 'irr' | 'payback';
  /** UPPERCASE chip label, e.g. "POWER REQUIREMENT". */
  label: string;
  /** Display value or `null` when the underlying intent is undecided
   *  / missing. `null` lets variants choose between "—" and omission. */
  value: string | null;
  /** True when `value` came from report/SED proxy rather than intent. */
  isEstimate?: boolean;
}

/**
 * The GENERIC site-metric ids `resolveCoverChips` produces for every report.
 * The financial money ids (`npv`/`irr`/`payback`) are deliberately NOT here —
 * they are resolved only by `applyFinancialCoverChips`, off the deterministic
 * `financialAnalysis.headline` stamp, and have no generic intent fallback.
 */
const ALL_CHIP_IDS: readonly CoverChip['id'][] = [
  'power', 'cooling', 'acres', 'btm', 'lcoe',
];

/** Financial Viability cover ids, in headline order. */
const FINANCIAL_CHIP_IDS: readonly CoverChip['id'][] = ['lcoe', 'npv', 'irr', 'payback'];

/** Report-type chip selection — filters variant defaults and sets order. */
interface ReportChipProfile {
  /** Chip ids to omit even when the variant would normally show them. */
  exclude?: readonly CoverChip['id'][];
  /** Preferred chip order within the variant subset (optional). */
  order?: readonly CoverChip['id'][];
}

const REPORT_TYPE_CHIP_PROFILES: Partial<Record<ReportTypeId, ReportChipProfile>> = {
  feasibility: {},
  'power-availability': {
    order: ['power', 'btm', 'lcoe', 'cooling', 'acres'],
  },
  interconnection: {
    exclude: ['lcoe'],
    order: ['power', 'cooling', 'acres', 'btm'],
  },
  'water-resource': {
    exclude: ['btm', 'lcoe'],
    order: ['cooling', 'acres', 'power'],
  },
  environmental: {
    exclude: ['btm', 'lcoe'],
    order: ['power', 'cooling', 'acres'],
  },
  'test-fit': {
    exclude: ['btm', 'lcoe'],
    order: ['acres', 'power', 'cooling'],
  },
  'supply-chain-risk': {
    exclude: ['acres', 'lcoe'],
    order: ['power', 'btm', 'cooling'],
  },
  'public-sentiment': {
    exclude: ['acres', 'btm', 'lcoe'],
    order: ['power', 'cooling'],
  },
  'executive-summary': {
    exclude: ['acres', 'lcoe'],
    order: ['power', 'btm', 'cooling'],
  },
  // v1.2.2 — Financial Viability cover leads with money metrics
  // (LCOE / NPV / IRR / Payback) instead of the generic POWER/COOLING pair.
  // `applyFinancialCoverChips` below OWNS this type's chip set outright — it
  // emits the dedicated money ids (D17), none of which is `power` (which
  // HeroImageCover filters out of the hero strip). The generic ids are all
  // excluded here so no site-metric chip leaks into the strip beside them.
  financial: {
    exclude: ['power', 'cooling', 'acres', 'btm'],
    order: ['lcoe'],
  },
  'custom-brief': {
    exclude: ['acres', 'lcoe'],
    order: ['power', 'btm', 'cooling'],
  },
};

const GRID_PROXY_REPORT_TYPES = new Set<ReportTypeId>([
  'feasibility',
  'interconnection',
  'power-availability',
]);

/**
 * Read the project intent off a report, preferring the
 * post-generation snapshot (`projectIntentAtGen`) over the soft
 * top-level `projectIntent` cast that some inline paths stamp.
 */
function readIntent(report: SiteRagReport): ProjectIntent | undefined {
  return deriveIntentForPlanningReport(report, (
    report.projectIntentAtGen
    ?? (report as { projectIntent?: ProjectIntent | undefined }).projectIntent
    ?? undefined
  ));
}

function readReportType(report: SiteRagReport): ReportTypeId {
  return report.reportType ?? 'feasibility';
}

function formatAcres(acres: number): string {
  const rounded = Math.round(acres * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
  return `${text} ac`;
}

function formatMw(mw: number): string {
  const rounded = Math.round(mw * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
  return `${text} MW`;
}

/** Acres resolver — prefers `intent.landAreaAcres` (exact), else the
 *  bucket field. Returns `null` when both are absent or zero. */
function effectiveAcresFromIntent(intent: ProjectIntent | undefined): string | null {
  if (!intent) return null;
  const exact = (intent as { landAreaAcres?: unknown }).landAreaAcres;
  if (typeof exact === 'number' && Number.isFinite(exact) && exact > 0) {
    return formatAcres(exact);
  }
  const bucket = (intent as { landAreaBucket?: unknown }).landAreaBucket;
  if (typeof bucket === 'string' && bucket.length > 0 && bucket !== 'undecided') {
    return String(bucket);
  }
  return null;
}

/** BTM resolver — surfaces a Primary / Considering / No chip from
 *  `intent.nuclearInterest` when set; `null` for undecided / missing. */
function effectiveBtmFromIntent(intent: ProjectIntent | undefined): string | null {
  if (!intent) return null;
  switch (intent.nuclearInterest) {
    case 'primary':     return 'Primary';
    case 'considering': return 'Considering';
    case 'no':          return 'No';
    case 'undecided':   return null;
    default:            return null;
  }
}

/** LCOE resolver — reads the report's pre-computed LCOE estimate
 *  (`report.lcoe`) when present. Falls through `null` when the
 *  report didn't run an LCOE pass. */
function effectiveLcoeFromReportField(report: SiteRagReport): string | null {
  const lcoe = (report as { lcoe?: unknown }).lcoe;
  if (typeof lcoe === 'number' && Number.isFinite(lcoe) && lcoe > 0) {
    const rounded = Math.round(lcoe * 10) / 10;
    const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
    return `$${text}/MWh`;
  }
  return null;
}

/** Verified grid capacity tier from stamped datasets — qualitative
 *  HIFLD proxy, never nameplate MW. */
function resolveGridCapacityProxy(report: SiteRagReport): string | null {
  const cap = report.datasets?.grid?.capacity;
  if (!cap) return null;
  if (String(cap).trim().toLowerCase() === 'unknown') return null;
  return `Est. ${cap} (HIFLD proxy)`;
}

function resolveEnvironmentalFloodChip(report: SiteRagReport): CoverChip {
  const flood = report.datasets?.land?.floodRisk;
  if (flood) {
    return { id: 'power', label: 'Flood risk', value: flood };
  }
  const nepa = report.nepaScreening?.determination?.trim();
  if (nepa) {
    return { id: 'power', label: 'NEPA screening', value: nepa };
  }
  return { id: 'power', label: 'Flood risk', value: null };
}

function resolveEnvironmentalWaterStressChip(report: SiteRagReport): CoverChip {
  const ws = report.datasets?.climate?.waterStress;
  if (ws) {
    return { id: 'cooling', label: 'Water stress', value: ws };
  }
  const authored = report.waterResources?.waterStressAnalysis?.trim();
  if (authored) {
    const parsed = parseWaterStressChipLabel(authored);
    if (parsed) {
      return { id: 'cooling', label: 'Water stress', value: parsed, isEstimate: true };
    }
  }
  return { id: 'cooling', label: 'Water stress', value: null };
}

const PHASE_TECH_MIX_TBD = 'Power source TBD across all phases';

const ONSITE_POWER_SOURCES: ReadonlySet<string> = new Set([
  'gas-cc',
  'gas-sc',
  'smr',
  'large-nuclear',
  'solar-bess',
  'wind-bess',
  'hybrid',
] as const);

type BtmStrategyKey = NonNullable<PowerAvailabilityPreferences['btmStrategy']>;
type ReportWithBtmStrategy = SiteRagReport & { btmStrategy?: BtmStrategyKey };

function btmStrategyToDecisionState(strategy: BtmStrategyKey) {
  switch (strategy) {
    case 'btm-only':
    case 'islanded':
      return 'btm-recommended' as const;
    case 'grid-only': return 'grid-recommended' as const;
    case 'hybrid': return 'hybrid-candidate' as const;
  }
}

/** Phased intent tech-mix summary — null when all phases TBD or legacy single-phase. */
function resolvePrimaryBtmFromIntent(intent: ProjectIntent | undefined): string | null {
  if (!intent) return null;
  const phases = effectivePhases(intent);
  if (!phases.isPhased) return null;
  if (phases.techMixSummary === PHASE_TECH_MIX_TBD) return null;
  return phases.techMixSummary;
}

function readReportBtmStrategy(report: SiteRagReport): BtmStrategyKey | undefined {
  const reportWithBtm = report as ReportWithBtmStrategy;
  if (reportWithBtm.btmStrategy) return reportWithBtm.btmStrategy;

  const atGen = report.typedPreferencesAtGen?.['power-availability']?.btmStrategy;
  if (atGen) return atGen;

  const live = report.typedPreferences?.['power-availability']?.btmStrategy;
  if (live) return live;

  const intent = readIntent(report);
  if (!intent) return undefined;
  return powerAvailabilityFromIntent(intent).btmStrategy;
}

/** Infer grid / hybrid / BTM posture from phased power mix and nuclear interest. */
function inferBtmPostureFromIntent(intent: ProjectIntent | undefined): BtmStrategyKey | undefined {
  if (!intent) return undefined;

  const fromNuclear = powerAvailabilityFromIntent(intent).btmStrategy;
  if (fromNuclear) return fromNuclear;

  const phases = effectivePhases(intent);
  if (!phases.isPhased || phases.powerSourceMix.length === 0) return undefined;

  const hasGrid = phases.powerSourceMix.includes('grid');
  const hasOnsite = phases.powerSourceMix.some(s => ONSITE_POWER_SOURCES.has(s));

  if (hasGrid && hasOnsite) return 'hybrid';
  if (hasGrid) return 'grid-only';
  if (hasOnsite) return 'btm-only';
  return undefined;
}

function resolvePostureLabelFromStrategy(strategy: BtmStrategyKey | undefined): string | null {
  if (!strategy) return null;
  return getDecisionStateLabel(btmStrategyToDecisionState(strategy));
}

/** Feasibility hero-image — leading BTM technology (intent-first, then report analysis). */
function resolveFeasibilityHeroPrimaryBtmChip(report: SiteRagReport): CoverChip {
  const intent = readIntent(report);
  // Humanized power-mix prose for the reader-facing cover — NOT the editor
  // notation ("AND:"/"OR:" + ", grid-connected") that `techMixSummary`
  // emits. COVER ROUND-2 routed the Classic/DataDense cooling + BTM chips
  // through this same `formatPrimaryBtmForCover` seam, so the raw
  // `techMixSummary` notation no longer reaches any cover surface.
  const fromIntent = formatPrimaryBtmForCover(intent);
  if (fromIntent) {
    return { id: 'power', label: 'Primary BTM', value: fromIntent };
  }

  const leading = pickLeadingBtmOption(report.btmOptions ?? []);
  const technology =
    leading?.technology?.trim() ||
    report.btmDetailedAnalysis?.[0]?.technology?.trim() ||
    null;
  if (!technology) {
    return { id: 'power', label: 'Primary BTM', value: null };
  }
  const risk = leading?.riskLevel?.trim();
  const value = risk ? `${technology} · ${risk} risk` : technology;
  return { id: 'power', label: 'Primary BTM', value };
}

/** Feasibility hero-image — BTM posture from strategy, intent, or decision state. */
function resolveFeasibilityHeroPostureChip(report: SiteRagReport): CoverChip {
  const intent = readIntent(report);
  const strategyLabel = resolvePostureLabelFromStrategy(readReportBtmStrategy(report));
  if (strategyLabel) {
    return { id: 'cooling', label: 'BTM posture', value: strategyLabel };
  }

  const inferredLabel = resolvePostureLabelFromStrategy(inferBtmPostureFromIntent(intent));
  if (inferredLabel) {
    return { id: 'cooling', label: 'BTM posture', value: inferredLabel };
  }

  if (intent?.nuclearInterest === 'primary') {
    return { id: 'cooling', label: 'BTM posture', value: 'BTM recommended' };
  }

  const state = getBtmDecisionState(report);
  if (state !== 'decision-pending') {
    return { id: 'cooling', label: 'BTM posture', value: getDecisionStateLabel(state) };
  }
  return { id: 'cooling', label: 'BTM posture', value: null };
}

function applyFeasibilityHeroImageChips(
  report: SiteRagReport,
  chips: CoverChip[],
): CoverChip[] {
  // DROP the "BTM posture: Hybrid candidate" chip from the feasibility hero
  // subset — it restates the Primary-BTM mix without adding decision value.
  // The `cooling` slot is removed entirely (not rendered as a "—" placeholder)
  // so the hero strip shows only the humanized Primary BTM chip.
  const byId = new Map(chips.map(chip => [chip.id, chip]));
  byId.set('power', resolveFeasibilityHeroPrimaryBtmChip(report));
  return chips
    .filter(chip => chip.id !== 'cooling')
    .map(chip => byId.get(chip.id) ?? chip);
}

/** Merge frozen-at-gen, live typed prefs, and intent inference for test-fit setup. */
function readTestFitPreferences(report: SiteRagReport): TestFitPreferences | undefined {
  const atGen = report.typedPreferencesAtGen?.['test-fit'];
  const live = report.typedPreferences?.['test-fit'];
  const intent = readIntent(report);
  const inferred = intent ? testFitFromIntent(intent) : {};
  const merged: TestFitPreferences = {
    ...inferred,
    ...live,
    ...atGen,
  };
  const hasAny = Object.values(merged).some(v => v !== undefined);
  return hasAny ? merged : undefined;
}

function resolveTestFitSiteAreaValue(report: SiteRagReport): { value: string; isEstimate: true } | null {
  const siteArea = report.siteBoundaryAggregate?.totalArea ?? report.siteArea;
  if (typeof siteArea === 'number' && Number.isFinite(siteArea) && siteArea > 0) {
    return { value: `Est. ${formatAcres(siteArea)}`, isEstimate: true };
  }
  const landAcres = report.datasets?.land?.acres;
  if (typeof landAcres === 'number' && Number.isFinite(landAcres) && landAcres > 0) {
    return { value: `Est. ${formatAcres(landAcres)}`, isEstimate: true };
  }
  return null;
}

function resolveTestFitFootprintChip(
  report: SiteRagReport,
  id: 'power' | 'acres',
): CoverChip {
  const prefs = readTestFitPreferences(report);
  const acres = prefs?.targetFootprintAcres;
  if (typeof acres === 'number' && Number.isFinite(acres) && acres > 0) {
    return { id, label: 'Target footprint', value: formatAcres(acres) };
  }
  const siteArea = resolveTestFitSiteAreaValue(report);
  if (siteArea) {
    return { id, label: 'Site area', value: siteArea.value, isEstimate: siteArea.isEstimate };
  }
  return { id, label: 'Target footprint', value: null };
}

function resolveTestFitPhasingValue(report: SiteRagReport): string | null {
  const prefs = readTestFitPreferences(report);
  if (prefs?.phasing === 'phased') {
    const count = prefs.phaseCount;
    return count != null && count > 0
      ? `Phased · ${count} phases`
      : 'Phased · ? phases';
  }
  if (prefs?.phasing === 'single') {
    return 'Single buildout';
  }
  const phaseLayout = report.testFitAnalysis?.phaseLayout?.trim();
  if (phaseLayout) {
    const lower = phaseLayout.toLowerCase();
    if (lower.includes('phase')) {
      return 'Multi-phase';
    }
    const excerpt = phaseLayout.length > 48
      ? `${phaseLayout.slice(0, 45).trim()}…`
      : phaseLayout;
    return excerpt;
  }
  return null;
}

function resolveTestFitPhasingChip(report: SiteRagReport): CoverChip {
  return {
    id: 'cooling',
    label: 'Phasing',
    value: resolveTestFitPhasingValue(report),
  };
}

function resolveTestFitSiteAreaChip(report: SiteRagReport): CoverChip {
  const siteArea = resolveTestFitSiteAreaValue(report);
  if (siteArea) {
    return { id: 'power', label: 'Site area', value: siteArea.value, isEstimate: siteArea.isEstimate };
  }
  return { id: 'power', label: 'Site area', value: null };
}

function applyTestFitHeroImageChips(
  report: SiteRagReport,
  chips: CoverChip[],
): CoverChip[] {
  const byId = new Map(chips.map(chip => [chip.id, chip]));
  byId.set('power', resolveTestFitFootprintChip(report, 'power'));
  byId.set('cooling', resolveTestFitPhasingChip(report));
  return chips.map(chip => byId.get(chip.id) ?? chip);
}

function applyTestFitClassicChips(
  report: SiteRagReport,
  chips: CoverChip[],
): CoverChip[] {
  const byId = new Map(chips.map(chip => [chip.id, chip]));
  byId.set('acres', resolveTestFitFootprintChip(report, 'acres'));
  byId.set('power', resolveTestFitSiteAreaChip(report));
  byId.set('cooling', resolveTestFitPhasingChip(report));
  return chips.map(chip => byId.get(chip.id) ?? chip);
}

/** Power-availability hero-image — leading BTM technology + LCOE (not generic cooling). */
function resolvePowerAvailabilityHeroPrimaryChip(report: SiteRagReport): CoverChip {
  const btm = resolveFeasibilityHeroPrimaryBtmChip(report);
  if (btm.value) return btm;

  const gridProxy = resolveGridCapacityProxy(report);
  if (gridProxy) {
    return {
      id: 'power',
      label: 'Grid capacity (proxy)',
      value: gridProxy,
      isEstimate: true,
    };
  }

  const intent = readIntent(report);
  const effPower = effectivePower(intent);
  if (effPower.bucket !== 'undecided' || effPower.isExact) {
    return { id: 'power', label: 'Power requirement', value: effPower.displayLabel };
  }

  return { id: 'power', label: 'Primary BTM', value: null };
}

/** Power-availability hero-image — LCOE with BTM posture / interest fallbacks. */
function resolvePowerAvailabilityHeroSecondaryChip(report: SiteRagReport): CoverChip {
  const lcoe = resolveLcoeChip(report);
  if (lcoe.value) {
    return {
      id: 'cooling',
      label: lcoe.label,
      value: lcoe.value,
      isEstimate: lcoe.isEstimate,
    };
  }

  // COVER ROUND-2: DROP the "BTM posture: Hybrid candidate" fallback from the
  // power-availability hero secondary slot (it restated the Primary-BTM mix
  // without adding decision value — same rationale as the feasibility hero,
  // which filters the cooling slot entirely). Fall through to the qualitative
  // BTM-interest signal, else collapse to a null LCOE chip.
  const btmIntent = effectiveBtmFromIntent(readIntent(report));
  if (btmIntent) {
    return { id: 'cooling', label: 'BTM interest', value: btmIntent };
  }

  return { id: 'cooling', label: 'LCOE', value: null };
}

function applyPowerAvailabilityHeroImageChips(
  report: SiteRagReport,
  chips: CoverChip[],
): CoverChip[] {
  const byId = new Map(chips.map(chip => [chip.id, chip]));
  byId.set('power', resolvePowerAvailabilityHeroPrimaryChip(report));
  byId.set('cooling', resolvePowerAvailabilityHeroSecondaryChip(report));
  return chips.map(chip => byId.get(chip.id) ?? chip);
}

function resolveEnvironmentalHabitatChip(report: SiteRagReport): CoverChip {
  const env = report.datasets?.environmental;
  if (env?.hasWetlands) {
    if (typeof env.wetlandAcreage === 'number' && env.wetlandAcreage > 0) {
      return {
        id: 'acres',
        label: 'Wetlands',
        value: `${formatAcres(env.wetlandAcreage)} on site`,
      };
    }
    return { id: 'acres', label: 'Wetlands', value: 'Present on site' };
  }
  if (env?.hasCriticalHabitat) {
    return { id: 'acres', label: 'Critical habitat', value: 'Overlap flagged' };
  }
  const aqi = report.datasets?.airQuality?.category?.trim();
  if (aqi) {
    return { id: 'acres', label: 'Air quality', value: aqi };
  }
  const acres = report.siteBoundaryAggregate?.totalArea ?? report.siteArea;
  if (typeof acres === 'number' && Number.isFinite(acres) && acres > 0) {
    return {
      id: 'acres',
      label: 'Study area',
      value: formatAcres(acres),
    };
  }
  return { id: 'acres', label: 'Wetlands', value: null };
}

function resolvePublicSentimentIndexChip(report: SiteRagReport): CoverChip {
  const score = report.sentimentIndex?.score;
  if (typeof score === 'number' && Number.isFinite(score)) {
    return { id: 'power', label: 'Sentiment index', value: `${Math.round(score)}/100` };
  }
  const meta = report.publicSentimentMeta;
  if (meta && meta.entryCount > 0) {
    return {
      id: 'power',
      label: 'Observations',
      value: `${meta.entryCount} entries`,
      isEstimate: true,
    };
  }
  return { id: 'power', label: 'Sentiment index', value: null };
}

function resolvePublicSentimentBandChip(report: SiteRagReport): CoverChip {
  const band = formatSentimentBand(report.sentimentIndex?.band);
  if (band) {
    return { id: 'cooling', label: 'Sentiment band', value: band };
  }
  const meta = report.publicSentimentMeta;
  if (meta) {
    const opposed = meta.opposedTechCount;
    if (opposed > 0) {
      return {
        id: 'cooling',
        label: 'Controversy flags',
        value: `${opposed} opposed tech${opposed === 1 ? '' : 's'}`,
        isEstimate: true,
      };
    }
    if (meta.sourcesAttempted > 0) {
      return {
        id: 'cooling',
        label: 'Sources scanned',
        value: `${meta.sourcesAttempted} source${meta.sourcesAttempted === 1 ? '' : 's'}`,
        isEstimate: true,
      };
    }
    if (meta.status === 'unavailable') {
      return { id: 'cooling', label: 'Scan status', value: 'No data' };
    }
  }
  return { id: 'cooling', label: 'Sentiment band', value: null };
}

function applyPublicSentimentCoverChips(
  report: SiteRagReport,
  chips: CoverChip[],
): CoverChip[] {
  const byId = new Map(chips.map(chip => [chip.id, chip]));
  byId.set('power', resolvePublicSentimentIndexChip(report));
  byId.set('cooling', resolvePublicSentimentBandChip(report));
  return chips.map(chip => byId.get(chip.id) ?? chip);
}

function resolvePowerChip(
  report: SiteRagReport,
  intent: ProjectIntent | undefined,
): CoverChip {
  if (readReportType(report) === 'public-sentiment') {
    return resolvePublicSentimentIndexChip(report);
  }

  if (readReportType(report) === 'environmental') {
    return resolveEnvironmentalFloodChip(report);
  }

  const phases = effectivePhases(intent);
  if (phases.isPhased) {
    const mwPart = phases.totalCapacityMW != null && phases.totalCapacityMW > 0
      ? `${formatMw(phases.totalCapacityMW)} · ${phases.phaseCount} phases`
      : phases.ultimateCapacityBucket != null
        ? `${POWER_BUCKET_LABEL[phases.ultimateCapacityBucket]} · ${phases.phaseCount} phases`
        : `${phases.phaseCount} phases · capacity TBD`;
    const codPart = phases.initialInServiceDate && phases.ultimateInServiceDate
      ? ` · ${phases.initialInServiceDate} → ${phases.ultimateInServiceDate}`
      : phases.initialInServiceDate && phases.ultimateCodTimeline
        ? ` · Phase 1 COD ${phases.initialInServiceDate}; ${phases.phases[phases.phases.length - 1]?.label || `Phase ${phases.phaseCount}`} ${TIMELINE_BUCKET_LABEL[phases.ultimateCodTimeline]} (planning target, not a COD)`
        : '';
    return {
      id: 'power',
      label: 'Phased rollout',
      value: `${mwPart}${codPart}`,
    };
  }

  const effPower = effectivePower(intent);
  if (effPower.bucket !== 'undecided' || effPower.isExact) {
    return { id: 'power', label: 'Power requirement', value: effPower.displayLabel };
  }

  if (phases.totalCapacityMW != null && phases.totalCapacityMW > 0) {
    return {
      id: 'power',
      label: 'Power requirement',
      value: formatMw(phases.totalCapacityMW),
    };
  }

  const reportType = readReportType(report);
  if (GRID_PROXY_REPORT_TYPES.has(reportType)) {
    const gridProxy = resolveGridCapacityProxy(report);
    if (gridProxy) {
      return {
        id: 'power',
        label: 'Grid capacity (proxy)',
        value: gridProxy,
        isEstimate: true,
      };
    }
  }

  return { id: 'power', label: 'Power requirement', value: null };
}

function resolveCoolingChip(
  report: SiteRagReport,
  intent: ProjectIntent | undefined,
): CoverChip {
  if (readReportType(report) === 'public-sentiment') {
    return resolvePublicSentimentBandChip(report);
  }

  if (readReportType(report) === 'environmental') {
    return resolveEnvironmentalWaterStressChip(report);
  }

  const phases = effectivePhases(intent);
  if (phases.isPhased) {
    // Reader-facing humanized prose (no "AND:"/"OR:" editor notation, no
    // ", grid-connected" suffix) — same seam the Hero cover uses. Falls back
    // to null when every phase is TBD so the chip collapses cleanly.
    const humanized = formatPrimaryBtmForCover(intent);
    return {
      id: 'cooling',
      label: 'Phase path',
      value: humanized,
    };
  }

  const coolingCanonical = normaliseCooling(intent?.coolingStrategy);
  if (coolingCanonical !== 'undecided') {
    return {
      id: 'cooling',
      label: 'Cooling',
      value: COOLING_TYPE_LABEL[coolingCanonical],
    };
  }

  const reportType = readReportType(report);
  if (reportType === 'water-resource' || reportType === 'environmental') {
    const ws = report.datasets?.climate?.waterStress;
    if (ws) {
      return {
        id: 'cooling',
        label: 'Water stress (proxy)',
        value: `Est. ${ws}`,
        isEstimate: true,
      };
    }
  }

  return { id: 'cooling', label: 'Cooling', value: null };
}

function resolveAcresChip(
  report: SiteRagReport,
  intent: ProjectIntent | undefined,
): CoverChip {
  if (readReportType(report) === 'environmental') {
    return resolveEnvironmentalHabitatChip(report);
  }

  // Geometry-FIRST: when a parcel polygon is drawn, the "Site area" chip
  // must agree with the in-plate area label (same live geometry). This
  // takes precedence over the intent's planning target — the intent is a
  // desired footprint, not the actual drawn parcel, and labelling it
  // "Site area" against a real boundary is what produced the ~10× cover
  // discrepancy. (Test-fit keeps its own "Target footprint" chip path.)
  const fromGeometry = geometryDerivedAcres(report);
  if (fromGeometry != null) {
    return { id: 'acres', label: 'Site area', value: formatAcres(fromGeometry) };
  }

  const fromIntent = effectiveAcresFromIntent(intent);
  if (fromIntent) {
    return { id: 'acres', label: 'Site area', value: fromIntent };
  }

  const siteArea = report.siteBoundaryAggregate?.totalArea ?? report.siteArea;
  if (typeof siteArea === 'number' && Number.isFinite(siteArea) && siteArea > 0) {
    return {
      id: 'acres',
      label: 'Site area',
      value: `Est. ${formatAcres(siteArea)}`,
      isEstimate: true,
    };
  }

  const landAcres = report.datasets?.land?.acres;
  if (typeof landAcres === 'number' && Number.isFinite(landAcres) && landAcres > 0) {
    return {
      id: 'acres',
      label: 'Site area',
      value: `Est. ${formatAcres(landAcres)}`,
      isEstimate: true,
    };
  }

  return { id: 'acres', label: 'Site area', value: null };
}

function resolveBtmChip(
  report: SiteRagReport,
  intent: ProjectIntent | undefined,
): CoverChip {
  const fromIntent = effectiveBtmFromIntent(intent);
  if (fromIntent) {
    return { id: 'btm', label: 'BTM', value: fromIntent };
  }

  // Humanized phased power-mix prose for the reader-facing cover — NOT the
  // editor "AND:"/"OR:" notation that `resolvePrimaryBtmFromIntent`
  // (techMixSummary) emits. Falls back to null when all phases are TBD.
  const phasedTech = formatPrimaryBtmForCover(intent);
  if (phasedTech) {
    return { id: 'btm', label: 'BTM', value: phasedTech };
  }

  const reportType = readReportType(report);
  if (reportType === 'power-availability' || reportType === 'feasibility') {
    const authored = report.btmDetailedAnalysis?.[0]?.technology?.trim();
    if (authored) {
      return { id: 'btm', label: 'BTM', value: `Est. ${authored}`, isEstimate: true };
    }
    const option = report.btmOptions?.[0]?.technology?.trim();
    if (option) {
      return { id: 'btm', label: 'BTM', value: `Est. ${option}`, isEstimate: true };
    }
  }

  return { id: 'btm', label: 'BTM', value: null };
}

function resolveLcoeChip(report: SiteRagReport): CoverChip {
  const fromField = effectiveLcoeFromReportField(report);
  if (fromField) {
    return { id: 'lcoe', label: 'LCOE', value: fromField };
  }

  const reportType = readReportType(report);
  if (reportType === 'water-resource' || reportType === 'environmental') {
    return { id: 'lcoe', label: 'LCOE', value: null };
  }

  const authored =
    report.lcoeDetailedAnalysis?.[0]?.lcoeResult?.trim()
    ?? report.btmDetailedAnalysis?.[0]?.lcoeRange?.trim();
  if (authored) {
    const formatted = formatApproxLcoeAuthored(authored);
    const value = formatted.includes('/MWh') ? formatted : `${formatted}/MWh`;
    return { id: 'lcoe', label: 'LCOE', value, isEstimate: true };
  }

  return { id: 'lcoe', label: 'LCOE', value: null };
}

/**
 * Build the canonical chip set for a report with report/SED fallbacks.
 * Order: [Power] [Cooling] [Acres] [BTM] [LCOE].
 */
export function resolveCoverChips(report: SiteRagReport): CoverChip[] {
  const intent = readIntent(report);
  return [
    resolvePowerChip(report, intent),
    resolveCoolingChip(report, intent),
    resolveAcresChip(report, intent),
    resolveBtmChip(report, intent),
    resolveLcoeChip(report),
  ];
}

export type CoverVariantId = 'classic' | 'hero-image' | 'minimal' | 'data-dense';

const CHIPS_BY_VARIANT: Readonly<Record<CoverVariantId, ReadonlyArray<CoverChip['id']>>> = {
  classic:      ['power', 'cooling', 'acres'],
  'hero-image': ['power', 'cooling'],
  minimal:      ['power'],
  'data-dense': ['power', 'cooling', 'acres', 'btm', 'lcoe'],
};

function resolveChipIdsForReport(
  report: SiteRagReport,
  variant: CoverVariantId,
): CoverChip['id'][] {
  const base = [...CHIPS_BY_VARIANT[variant]];
  const profile = REPORT_TYPE_CHIP_PROFILES[readReportType(report)] ?? {};
  const filtered = profile.exclude
    ? base.filter(id => !profile.exclude!.includes(id))
    : base;

  if (!profile.order) return filtered;

  const ordered: CoverChip['id'][] = [];
  for (const id of profile.order) {
    if (filtered.includes(id) && !ordered.includes(id)) ordered.push(id);
  }
  for (const id of filtered) {
    if (!ordered.includes(id)) ordered.push(id);
  }
  return ordered;
}

const CHIP_LABELS: Record<CoverChip['id'], string> = {
  power: 'Power requirement',
  cooling: 'Cooling',
  acres: 'Site area',
  btm: 'BTM',
  lcoe: 'LCOE',
  npv: 'NPV',
  irr: 'IRR',
  payback: 'Payback',
};

function firstLine(value: string | undefined): string | null {
  if (!value?.trim()) return null;
  const line = value.split('\n').map(p => p.trim()).find(Boolean);
  return line ?? null;
}

function applyExecutiveSummaryCoverChips(
  report: SiteRagReport,
  chips: CoverChip[],
  variant: CoverVariantId,
): CoverChip[] {
  const verdictLabel = tierLabel(
    getDisplayVerdict(report).tier,
    'executive-summary',
    'overall',
  );
  const btmDecision = firstLine(
    report.btmDecisionSummary ?? report.conclusion?.btmDecisionSummary,
  );
  const riskCount = report.executiveSummaryCriticalRisks?.length ?? 0;
  const btmViability = firstLine(report.btmViabilityAssessment);
  const sourceCount = report.executiveSummarySourcesUsed?.length ?? 0;

  const chipIds: CoverChip['id'][] = variant === 'data-dense'
    ? ['power', 'cooling', 'acres', 'btm', 'lcoe']
    : variant === 'minimal'
      ? ['power']
      : variant === 'hero-image'
        ? ['power', 'cooling']
        : ['power', 'btm', 'cooling'];

  const byId = new Map(chips.map(chip => [chip.id, chip]));

  byId.set('power', {
    id: 'power',
    label: 'Overall verdict',
    value: verdictLabel === '—' ? null : verdictLabel,
  });
  byId.set('btm', {
    id: 'btm',
    label: 'BTM decision',
    value: btmDecision,
  });
  byId.set('cooling', {
    id: 'cooling',
    label: 'Critical risks',
    value: riskCount > 0 ? `${riskCount} flagged` : null,
  });

  if (variant === 'data-dense') {
    byId.set('acres', {
      id: 'acres',
      label: 'BTM viability',
      value: btmViability,
    });
    byId.set('lcoe', {
      id: 'lcoe',
      label: 'Sources synthesized',
      value: sourceCount > 0 ? `${sourceCount} reports` : null,
    });
  }

  return chipIds.map(id => byId.get(id) ?? { id, label: CHIP_LABELS[id] ?? id, value: null });
}

function applyCustomBriefCoverChips(
  report: SiteRagReport,
  chips: CoverChip[],
  variant: CoverVariantId,
): CoverChip[] {
  const briefObjective = firstLine(report.customBriefObjective ?? report.customBriefName);
  const btmDecision = firstLine(
    report.btmDecisionSummary ?? report.conclusion?.btmDecisionSummary,
  );
  const answerCount = report.customBriefAnswers?.length ?? 0;
  const briefName = firstLine(report.customBriefName);
  const counterparties = firstLine(report.customBriefCounterparties ?? report.customBriefKeyDates);

  const chipIds: CoverChip['id'][] = variant === 'data-dense'
    ? ['power', 'cooling', 'acres', 'btm', 'lcoe']
    : variant === 'minimal'
      ? ['power']
      : variant === 'hero-image'
        ? ['power', 'cooling']
        : ['power', 'btm', 'cooling'];

  const byId = new Map(chips.map(chip => [chip.id, chip]));

  byId.set('power', {
    id: 'power',
    label: 'Brief objective',
    value: briefObjective,
  });
  byId.set('btm', {
    id: 'btm',
    label: 'BTM decision',
    value: btmDecision,
  });
  byId.set('cooling', {
    id: 'cooling',
    label: 'Questions answered',
    value: answerCount > 0 ? `${answerCount} answered` : null,
  });

  if (variant === 'data-dense') {
    byId.set('acres', {
      id: 'acres',
      label: 'Brief name',
      value: briefName,
    });
    byId.set('lcoe', {
      id: 'lcoe',
      label: 'Counterparties',
      value: counterparties,
    });
  }

  return chipIds.map(id => byId.get(id) ?? { id, label: CHIP_LABELS[id] ?? id, value: null });
}

/** Format a USD NPV as a compact `$NNM` / `$N.NB` chip value. `null` on
 *  missing / non-finite. */
function formatNpvUsd(npvUsd: number | null | undefined): string | null {
  if (typeof npvUsd !== 'number' || !Number.isFinite(npvUsd)) return null;
  const abs = Math.abs(npvUsd);
  const sign = npvUsd < 0 ? '-' : '';
  if (abs >= 1e9) {
    const b = abs / 1e9;
    return `${sign}$${(Math.round(b * 10) / 10).toString()}B`;
  }
  const m = abs / 1e6;
  return `${sign}$${Math.round(m).toString()}M`;
}

/** Format an IRR percentage as `NN.N%`. `null` on missing / non-finite. */
function formatIrrPct(irrPct: number | null | undefined): string | null {
  if (typeof irrPct !== 'number' || !Number.isFinite(irrPct)) return null;
  const rounded = Math.round(irrPct * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString()}%`;
}

/** Format the project-finance LCOE as `$NN/MWh`. `null` on missing / ≤0. */
function formatFinancialLcoe(lcoeUsdPerMwh: number | null | undefined): string | null {
  if (typeof lcoeUsdPerMwh !== 'number' || !Number.isFinite(lcoeUsdPerMwh) || lcoeUsdPerMwh <= 0) {
    return null;
  }
  const rounded = Math.round(lcoeUsdPerMwh * 10) / 10;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
  return `$${text}/MWh`;
}

/** Format payback in years as `N.N yr`. `null` on missing / ≤0. */
function formatPaybackYears(years: number | null | undefined): string | null {
  if (typeof years !== 'number' || !Number.isFinite(years) || years <= 0) return null;
  const rounded = Math.round(years * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString()} yr`;
}

/**
 * v1.2.2 — Financial Viability cover chips. Reads the deterministic
 * `report.financialAnalysis.headline` stamp (LCOE / NPV / IRR / payback) onto
 * DEDICATED money ids, none of which is `power` (so they survive the hero
 * cover's `power`-id filter):
 *   - lcoe    → LCOE ($/MWh)   (every variant)
 *   - npv     → NPV  ($M/$B)   (hero slot 2 / classic / data-dense)
 *   - irr     → IRR (%)        (classic / data-dense)
 *   - payback → Payback (yr)   (data-dense only)
 *
 * D17 (audit 2026-08-19): these used to be keyed onto `btm` / `cooling` /
 * `acres` — ids whose tooltip text explains nuclear interest, cooling strategy
 * and land acres — so the NPV chip's "how is this calculated?" popover
 * described the site's cooling strategy. Dedicated ids mean the label, the
 * value and the explanation in `coverChipExplanations.ts` all name the same
 * metric. Each value is null-guarded so a thin stamp collapses the strip
 * cleanly.
 */
function applyFinancialCoverChips(
  report: SiteRagReport,
  chips: CoverChip[],
  variant: CoverVariantId,
): CoverChip[] {
  const headline = report.financialAnalysis?.headline;
  const lcoe = formatFinancialLcoe(headline?.lcoeUsdPerMwh);
  const npv = formatNpvUsd(headline?.npvUsd);
  const irr = formatIrrPct(headline?.irrPct);
  const payback = formatPaybackYears(headline?.paybackYears);

  // 2 visible hero slots = LCOE + NPV; data-dense carries the full money rail.
  const chipIds: CoverChip['id'][] = variant === 'data-dense'
    ? [...FINANCIAL_CHIP_IDS]
    : variant === 'minimal'
      ? ['lcoe']
      : variant === 'hero-image'
        ? ['lcoe', 'npv']
        : ['lcoe', 'npv', 'irr'];

  const byId = new Map(chips.map(chip => [chip.id, chip]));

  byId.set('lcoe', { id: 'lcoe', label: CHIP_LABELS.lcoe, value: lcoe });
  byId.set('npv', { id: 'npv', label: CHIP_LABELS.npv, value: npv });
  byId.set('irr', { id: 'irr', label: CHIP_LABELS.irr, value: irr });
  byId.set('payback', { id: 'payback', label: CHIP_LABELS.payback, value: payback });

  return chipIds.map(id => byId.get(id) ?? { id, label: CHIP_LABELS[id] ?? id, value: null });
}

/**
 * Report-type-aware chip resolver — combines intent, verified report
 * fallbacks, and per-type chip subset overrides for a cover variant.
 */
export function resolveCoverChipsForReport(
  report: SiteRagReport,
  variant: CoverVariantId,
): CoverChip[] {
  const all = resolveCoverChips(report);
  const byId = new Map(all.map(chip => [chip.id, chip]));
  const ids = resolveChipIdsForReport(report, variant);

  const chips = ids.map(id => {
    const found = byId.get(id);
    return found ?? { id, label: CHIP_LABELS[id] ?? id.toUpperCase(), value: null };
  });

  const reportType = readReportType(report);
  if (reportType === 'feasibility' && variant === 'hero-image') {
    return applyFeasibilityHeroImageChips(report, chips);
  }
  if (reportType === 'test-fit' && variant === 'hero-image') {
    return applyTestFitHeroImageChips(report, chips);
  }
  if (reportType === 'test-fit' && variant === 'classic') {
    return applyTestFitClassicChips(report, chips);
  }
  if (reportType === 'power-availability' && variant === 'hero-image') {
    return applyPowerAvailabilityHeroImageChips(report, chips);
  }
  if (reportType === 'public-sentiment') {
    return applyPublicSentimentCoverChips(report, chips);
  }
  if (reportType === 'executive-summary') {
    return applyExecutiveSummaryCoverChips(report, chips, variant);
  }
  if (reportType === 'financial') {
    return applyFinancialCoverChips(report, chips, variant);
  }
  if (reportType === 'custom-brief') {
    return applyCustomBriefCoverChips(report, chips, variant);
  }

  return chips;
}

/** @deprecated Prefer `resolveCoverChipsForReport` — kept for call-site compat. */
export function resolveCoverChipsForVariant(
  report: SiteRagReport,
  variant: CoverVariantId,
): CoverChip[] {
  return resolveCoverChipsForReport(report, variant);
}

/** Exported for tests — canonical chip id list. */
export const COVER_CHIP_IDS = ALL_CHIP_IDS;
